package com.fantasytenis.web.screens.contests

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.scoring.entity.LeaderboardEntry
import com.fantasytenis.shared.core.realtime.ConnectionState
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.isMobile
import com.fantasytenis.web.hooks.useContestSubscription
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.textTransform
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen displaying the leaderboard for a contest with live updates.
 */
@Composable
fun ContestLeaderboardScreen(contestId: String) {
    val scope = rememberCoroutineScope()
    val currentUserId = SharedData.currentUser.collectAsState().value?.id
    var contest by remember { mutableStateOf<ContestEntity?>(null) }
    var entries by remember { mutableStateOf<List<LeaderboardEntry>>(emptyList()) }
    var entryEntitiesCache by remember { mutableStateOf<List<EntryEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentPage by remember { mutableStateOf(0) }
    var lastUpdateTime by remember { mutableStateOf<Long?>(null) }
    val pageSize = 50

    // Initialize realtime connection
    val realtimeState = useRealtime()
    val isMobileView = isMobile()

    // Subscribe to contest updates
    useContestSubscription(
        contestId = contestId,
        onLeaderboardUpdate = { update ->
            // Update entries with live data
            val updatedEntries = entries.toMutableList()
            update.entries.forEach { liveEntry ->
                val index = updatedEntries.indexOfFirst { it.userId == liveEntry.userId }
                if (index >= 0) {
                    updatedEntries[index] = LeaderboardEntry(
                        userId = liveEntry.userId,
                        userName = liveEntry.displayName,
                        rank = liveEntry.rank,
                        previousRank = liveEntry.previousRank,
                        points = liveEntry.totalPoints,
                        isCurrentUser = updatedEntries[index].isCurrentUser,
                        payout = liveEntry.payoutAmount
                    )
                }
            }
            // Re-sort by rank
            entries = updatedEntries.sortedBy { it.rank }
            lastUpdateTime = update.timestamp
        },
        onEntryUpdate = { update ->
            // Update specific user's entry
            val updatedEntries = entries.toMutableList()
            val index = updatedEntries.indexOfFirst { it.userId == update.userId }
            if (index >= 0) {
                val entry = updatedEntries[index]
                updatedEntries[index] = entry.copy(
                    rank = update.rank,
                    previousRank = update.previousRank,
                    points = update.totalPoints
                )
                entries = updatedEntries.sortedBy { it.rank }
            }
        }
    )

    // Load data
    LaunchedEffect(contestId, currentPage) {
        isLoading = true
        error = null

        // Load contest details
        DependencyContainer.getContestUseCase(contestId).fold(
            onSuccess = { contest = it },
            onFailure = { error = it.message }
        )

        // Load leaderboard
        DependencyContainer.getContestLeaderboardUseCase(
            contestId = contestId,
            limit = pageSize,
            offset = currentPage * pageSize
        ).fold(
            onSuccess = { entryEntities ->
                entryEntitiesCache = entryEntities
                entries = entryEntities.mapIndexed { index, entry ->
                    LeaderboardEntry(
                        userId = entry.userId,
                        userName = entry.formattedDisplayName,
                        rank = entry.rank ?: (index + 1),
                        previousRank = entry.rank ?: (index + 1),
                        points = entry.totalPoints,
                        isCurrentUser = entry.userId == currentUserId,
                        payout = entry.payoutAmount,
                        username = entry.username,
                        avatarUrl = entry.avatarUrl,
                        entryId = entry.id
                    )
                }
            },
            onFailure = { error = it.message }
        )

        isLoading = false
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header with ResponsiveHeader for mobile support
        ResponsiveHeader(
            title = "Leaderboard",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.ContestDetail(contestId)) },
            customActions = {
                // Contest name (desktop only - hidden in mobile hamburger menu)
                contest?.let {
                    Span({
                        style {
                            color(Color("rgba(255, 255, 255, 0.8)"))
                            fontSize(14.px)
                        }
                    }) {
                        Text(it.name)
                    }
                }

                // Live connection indicator
                if (realtimeState.isConnected && contest?.status == ContestStatus.LIVE) {
                    LiveIndicator()
                }
            }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(900.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.getContestLeaderboardUseCase(contestId).fold(
                            onSuccess = { entryEntities ->
                                entryEntitiesCache = entryEntities
                                entries = entryEntities.mapIndexed { index, entry ->
                                    LeaderboardEntry(
                                        userId = entry.userId,
                                        userName = entry.formattedDisplayName,
                                        rank = entry.rank ?: (index + 1),
                                        previousRank = entry.rank ?: (index + 1),
                                        points = entry.totalPoints,
                                        isCurrentUser = entry.userId == currentUserId,
                                        payout = entry.payoutAmount,
                                        username = entry.username,
                                        avatarUrl = entry.avatarUrl,
                                        entryId = entry.id
                                    )
                                }
                                isLoading = false
                            },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                else -> {
                    // Contest stats
                    contest?.let { ContestStatsBar(it) }

                    // Last update time
                    lastUpdateTime?.let { time ->
                        Div({
                            style {
                                textAlign("right")
                                fontSize(11.px)
                                color(Colors.TextSecondary)
                                marginBottom(8.px)
                            }
                        }) {
                            Text("Last updated: ${formatTime(time)}")
                        }
                    }

                    // Leaderboard table
                    LeaderboardTable(
                        entries = entries,
                        contestId = contestId,
                        startRank = currentPage * pageSize + 1,
                        isMobile = isMobileView,
                        onEntryClick = { entryId ->
                            NavigationState.navigate(Route.ContestEntryView(contestId, entryId))
                        }
                    )

                    // Pagination
                    if (entries.size >= pageSize || currentPage > 0) {
                        Pagination(
                            currentPage = currentPage,
                            hasMore = entries.size >= pageSize,
                            onPrevious = { if (currentPage > 0) currentPage-- },
                            onNext = { if (entries.size >= pageSize) currentPage++ }
                        )
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun ContestStatsBar(contest: ContestEntity) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(120px, 1fr))")
            gap(16.px)
            marginBottom(24.px)
        }
    }) {
        StatBox("Entries", "${contest.currentEntries} / ${contest.maxEntries}")
        StatBox("Prize Pool", "$${contest.prizePool}")
        val showPaidGames = SharedData.showPaidGames.value
        StatBox("Entry Fee", if (contest.entryFee == 0.0 && showPaidGames) "FREE" else if (contest.entryFee == 0.0) "$0" else "$${contest.entryFee}")
    }
}

@Composable
private fun StatBox(label: String, value: String) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            padding(16.px)
            borderRadius(8.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(4.px)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun LeaderboardTable(
    entries: List<LeaderboardEntry>,
    contestId: String,
    startRank: Int,
    isMobile: Boolean,
    onEntryClick: (entryId: String) -> Unit
) {
    // Responsive grid columns: mobile hides the Change column
    val gridColumns = if (isMobile) "40px 1fr 70px" else "60px 1fr 100px 80px"

    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Table header
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", gridColumns)
                padding(if (isMobile) 10.px else 12.px, if (isMobile) 12.px else 16.px)
                backgroundColor(Colors.SurfaceVariant)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
                fontSize(if (isMobile) 11.px else 12.px)
                fontWeight(600)
                color(Colors.TextSecondary)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Span { Text(if (isMobile) "#" else "Rank") }
            Span { Text("Player") }
            Span({
                style { textAlign("right") }
            }) { Text(if (isMobile) "Pts" else "Points") }
            if (!isMobile) {
                Span({
                    style { textAlign("right") }
                }) { Text("Change") }
            }
        }

        // Table rows
        if (entries.isEmpty()) {
            Div({
                style {
                    padding(32.px)
                    textAlign("center")
                    color(Colors.TextSecondary)
                }
            }) {
                Text("No entries yet")
            }
        } else {
            entries.forEachIndexed { index, entry ->
                val displayRank = entry.rank

                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", gridColumns)
                        padding(if (isMobile) 10.px else 12.px, if (isMobile) 12.px else 16.px)
                        alignItems(AlignItems.Center)
                        backgroundColor(if (entry.isCurrentUser) Colors.Highlight.Background else Colors.White)
                        borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
                        property("transition", "background-color 0.2s")
                        cursor("pointer")
                        // Ensure minimum touch target height on mobile
                        if (isMobile) {
                            minHeight(48.px)
                        }
                    }
                    entry.entryId?.let { entryId ->
                        onClick { onEntryClick(entryId) }
                    }
                    onMouseOver {
                        (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                            if (entry.isCurrentUser) "rgb(200, 230, 250)" else "rgb(248, 248, 248)"
                    }
                    onMouseOut {
                        (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                            if (entry.isCurrentUser) "rgb(227, 242, 253)" else "rgb(255, 255, 255)"
                    }
                }) {
                    // Rank badge (smaller on mobile)
                    RankBadge(displayRank, compact = isMobile)

                    // Player with avatar
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(if (isMobile) 8.px else 12.px)
                            overflow("hidden")
                        }
                    }) {
                        // Avatar (smaller on mobile)
                        AvatarDisplay(
                            avatarUrl = entry.avatarUrl,
                            initials = entry.initials,
                            size = if (isMobile) AvatarSize.XSMALL else AvatarSize.SMALL
                        )

                        // Name and payout
                        Div({
                            style {
                                overflow("hidden")
                                minWidth(0.px) // Allow text to truncate
                            }
                        }) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(4.px)
                                }
                            }) {
                                Span({
                                    style {
                                        fontWeight(if (entry.isCurrentUser) 600 else 500)
                                        fontSize(if (isMobile) 13.px else 14.px)
                                        color(Colors.TextPrimary)
                                        property("white-space", "nowrap")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) {
                                    Text(entry.displayName)
                                }
                                if (entry.isCurrentUser) {
                                    Span({
                                        style {
                                            fontSize(10.px)
                                            color(Colors.Highlight.Text)
                                            fontWeight(500)
                                        }
                                    }) {
                                        Text("(You)")
                                    }
                                }
                            }
                            // Payout if any
                            if (entry.payout > 0) {
                                Div({
                                    style {
                                        fontSize(11.px)
                                        color(Colors.SuccessDark)
                                        fontWeight(500)
                                    }
                                }) {
                                    Text("Wins $${entry.payout}")
                                }
                            }
                        }
                    }

                    // Points with inline rank change on mobile
                    Div({
                        style {
                            textAlign("right")
                        }
                    }) {
                        // Points
                        Div({
                            style {
                                fontSize(if (isMobile) 14.px else 16.px)
                                fontWeight(600)
                                color(Colors.Primary)
                            }
                        }) {
                            Text("${entry.points.toInt()}")
                        }

                        // On mobile, show rank change inline below points
                        if (isMobile) {
                            val change = entry.rankChange
                            if (change != 0) {
                                Div({
                                    style {
                                        fontSize(10.px)
                                        fontWeight(500)
                                        color(if (change > 0) Colors.Success else Colors.Error)
                                    }
                                }) {
                                    Text(if (change > 0) "+$change" else "$change")
                                }
                            }
                        }
                    }

                    // Rank change column (desktop only)
                    if (!isMobile) {
                        Div({
                            style {
                                textAlign("right")
                            }
                        }) {
                            val change = entry.rankChange
                            when {
                                change > 0 -> {
                                    Span({
                                        style {
                                            color(Colors.Success)
                                            fontWeight(500)
                                            fontSize(14.px)
                                        }
                                    }) {
                                        Text("+$change")
                                    }
                                }
                                change < 0 -> {
                                    Span({
                                        style {
                                            color(Colors.Error)
                                            fontWeight(500)
                                            fontSize(14.px)
                                        }
                                    }) {
                                        Text("$change")
                                    }
                                }
                                else -> {
                                    Span({
                                        style {
                                            color(Colors.TextSecondary)
                                            fontSize(14.px)
                                        }
                                    }) {
                                        Text("-")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RankBadge(rank: Int, compact: Boolean = false) {
    val (bgColor, textColor) = when (rank) {
        1 -> Colors.Medal.GoldBackground to Colors.Medal.GoldText
        2 -> Colors.Medal.SilverBackground to Colors.Medal.SilverText
        3 -> Colors.Medal.BronzeBackground to Colors.Medal.BronzeText
        else -> Colors.SurfaceVariant to Colors.TextPrimary
    }

    val size = if (compact) 28.px else 36.px

    Div({
        style {
            width(size)
            height(size)
            borderRadius(50.percent)
            backgroundColor(bgColor)
            color(textColor)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontSize(if (compact) 12.px else 14.px)
            fontWeight(600)
            flexShrink(0)
        }
    }) {
        Text("$rank")
    }
}

@Composable
private fun Pagination(
    currentPage: Int,
    hasMore: Boolean,
    onPrevious: () -> Unit,
    onNext: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(16.px)
            marginTop(24.px)
        }
    }) {
        Button(
            text = "Previous",
            onClick = onPrevious,
            variant = if (currentPage > 0) "secondary" else "disabled"
        )

        Span({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text("Page ${currentPage + 1}")
        }

        Button(
            text = "Next",
            onClick = onNext,
            variant = if (hasMore) "secondary" else "disabled"
        )
    }
}

private fun formatTime(timestamp: Long): String {
    val now = js("Date.now()") as Long
    val diff = (now - timestamp) / 1000 // seconds

    return when {
        diff < 5 -> "just now"
        diff < 60 -> "${diff}s ago"
        diff < 3600 -> "${diff / 60}m ago"
        else -> "${diff / 3600}h ago"
    }
}

@Composable
private fun LiveIndicator() {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            backgroundColor(Color("rgba(76, 175, 80, 0.2)"))
            padding(4.px, 8.px)
            borderRadius(12.px)
        }
    }) {
        // Pulsing green dot
        Div({
            style {
                width(8.px)
                height(8.px)
                borderRadius(50.percent)
                backgroundColor(Colors.Success)
                property("animation", "pulse 2s infinite")
            }
        })
        Span({
            style {
                fontSize(11.px)
                color(Colors.White)
                fontWeight(500)
            }
        }) {
            Text("LIVE")
        }
    }
}
